import React, { useEffect, useMemo, useState } from 'react';
import { useTitle } from '@arema/components/Hooks';
import { Field, Groupper, Icon, Input, Button, Table, Message, Header, useFrontier, Loader } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../AdminHooks';
import { LoginSource, SetLoading, ShortUser, SystemAccessType, SystemAccessTypes } from '@arema/components/Classes';
import { bindFormChange } from '@arema/components/Util';
import { Link } from 'react-router-dom';
import { SystemAccessRow } from '../../components';
import Validator from '@arema/components/Validator';
import API from '../../API';
import classNames from 'classnames';
import UserAccess from '../../UserAccess';

const EXTERNAL_EMAIL_CHECK : SystemAccessTypes[] = ['ADMIN', 'OPERATIONS'];
const SYSTEM_ACCESS : { system: SystemAccessTypes, access: number }[] = [{
	system: 'ADMIN',
	access: UserAccess.USERS.GIVE_ADMIN_ACCESS
}, {
	system: 'OPERATIONS',
	access: UserAccess.USERS.GIVE_OPERATIONS_ACCESS,
}, {
	system: 'PDV',
	access: UserAccess.USERS.GIVE_PDV_ACCESS,
}, {
	system: 'REPORTS',
	access: UserAccess.USERS.GIVE_REPORTS_ACCESS,
}, {
	system: 'SCANNER',
	access: UserAccess.USERS.GIVE_SCANNER_ACCESS,
}]

var UserCreate = ()=>{
	var { t } = useTranslation();
	var { access, hasAccess } = useUser();
	var { setTitle } = useTitle();
	var { Toast } = useFrontier();
	var [searchTimeout, setSearchTimeout] = useState(null);
	var [validEmail, setValidEmail] = useState<{ email: string, searching: boolean, user: ShortUser, error: boolean }>(null);
	var [systemAccess, setSystemAccess] = useState<SystemAccessType>({});
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [created, setCreated] = useState<boolean>(null);
	var [userData, setUserData] = useState<{
		user_id?: number,
		first_name: string,
		last_name: string,
		email: string,
		password: string,
		send_password: boolean
	}>({
		user_id: null,
		first_name: '',
		last_name: '',
		email: '',
		password: '',
		send_password: true
	})

	useEffect(()=>{
		setTitle('Crear usuario');
	}, []);

	var onFormChange = bindFormChange(userData, setUserData);

	var searchEmail = (email: string)=>{
		API.getUserFromEmail(email).then(res=>{
			if(res.error) return setValidEmail({ email, user: null, searching: false, error: true });
			setValidEmail({ email, user: res.data, searching: false, error: false });
		}).catch(err=>{
			return setValidEmail({ email, user: null, searching: false, error: true });
		});
	}

	var onEmailChange = (v: string)=>{
		onFormChange('email')(v);
		if(searchTimeout) clearTimeout(searchTimeout);

		if(Validator.validate([{ rule: 'email' }], v)){
			if(validEmail && validEmail.email.trim().toLowerCase()==v.trim().toLowerCase()){
				return;
			}
			setValidEmail({ email: v, searching: true, user: null, error: false });
			setSearchTimeout(setTimeout(()=>{
				searchEmail(v);
			}, 1000))
		}else{
			setValidEmail(null);
		}
	}

	var canGrantAccess = useMemo(()=>{
		for(var i of SYSTEM_ACCESS){
			if(hasAccess(i.access)) return true;
		}
		return false;
	}, [access]);

	var warnEmailExternal = useMemo(()=>{
		if(!validEmail || validEmail.searching) return false;
		for(var i of EXTERNAL_EMAIL_CHECK){
			if(systemAccess[i]){
				if(!validEmail.email.trim().toLowerCase().endsWith('arema.mx')){
					return true;
				}
			}
		}
		return false;
	}, [validEmail, systemAccess]);

	var user_systems = useMemo(()=>{
		var systems : number[] = [];
		for(var i of SYSTEM_ACCESS){
			if(systemAccess[i.system]){
				systems.push(LoginSource[i.system])
			}
		}
		return systems;
	}, [systemAccess]);

	var sendCreate = (setLoading: SetLoading)=>{
		if(!validEmail) return;
		if(validEmail.user) return;
		var { valid, prompts } = Validator(userData, {
			first_name: [{ rule: 'minLength', params: [3], label: 'Nombre' }],
			last_name: [{ rule: 'minLength', params: [3], label: 'Apellido' }],
			email: ['email'],
			password: !userData.send_password ? [{ rule: 'password', label: 'Contraseña' }] : []
		});
		setErrorPrompts(prompts);
		if(!valid) return;

		var systems : number[] = [];
		for(var i of SYSTEM_ACCESS){
			if(systemAccess[i.system]){
				systems.push(LoginSource[i.system])
			}
		}

		setLoading(true);
		API.createUser({
			first_name: userData.first_name,
			last_name: userData.last_name,
			email: userData.email,
			password: userData.send_password ? null : userData.password
		}, systems).then(res=>{
			if(res.error) return setErrorPrompts([res.message]);
			setUserData({
				...userData,
				user_id: res.data.user_id,
			})
			setCreated(true);
			Toast.success('Usuario creado!');
		}).catch(err=>{
			setErrorPrompts(['Hubo un error inesperado creando el usuario (LCL-1)'])
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onSystemAccessChange = bindFormChange(systemAccess, setSystemAccess);
	
	if(created){
		return <Groupper style={{ maxWidth: 500 }} title={'Usuario creado'} titleCentered actions={(
			<Button text='Ver usuario' color='black' as={Link} to={`/users/${userData.user_id}`} />
		)}>
			<Icon name='user-circle' style={{ textAlign: 'center', display: 'block', fontSize: 80, lineHeight: '80px', width: '100%' }} />
			<Header text={`${userData.first_name} ${userData.last_name}`} style={{ fontSize: 20, marginBottom: 15 }} subtext={'Usuario creado'} subtextStyle={{ fontSize: 14, marginTop: 0 }} />
			<Table details striped>
				<Table.Head title='Nuevo usuario' />
				<Table.Body>
					<Table.Row data={['ID', userData.user_id]} />
					<Table.Row data={['Nombre', `${userData.first_name} ${userData.last_name}`]} />
					<Table.Row data={['Correo', userData.email.toLowerCase()]} />
					<Table.Row data={['Accesos', user_systems.map(a=>SystemAccessRow.Locale[a]).join(', ')]} />
				</Table.Body>
			</Table>

			{!!(systemAccess.ADMIN || systemAccess.PDV || systemAccess.REPORTS) && (
				<Message centered style={{ marginTop: 20 }}>
					El usuario tiene acceso para entrar a los sistemas <b>{user_systems.map(a=>SystemAccessRow.Locale[a]).join(', ')}</b>, pero no tiene permiso para realizar acciones.
					<br />
					<b>Para esto hay que darle permiso a el usuario para realizar acciones en estos sistemas</b>
				</Message>
			)}
		</Groupper>
	}

	return <div className="ar user-create-screen">
		<Groupper title={'Crear usuario'} style={{ maxWidth: 500 }} actions={(
			<Button text='Guardar' color='black' onClick={sendCreate} disabled={!validEmail || validEmail?.searching || !!validEmail?.user} />
		)}>
			<Field amount={2}>
				<Input label='Nombre' value={userData.first_name} onChange={onFormChange('first_name')} />
				<Input label='Apellido' value={userData.last_name} onChange={onFormChange('last_name')} />
			</Field>
			<Input
				label='Correo electrónico'
				value={userData.email} 
				onChange={onEmailChange}
				type={validEmail && !validEmail?.searching ? (
					validEmail.error ? 'error' : (validEmail.user || warnEmailExternal) ? 'warning' : null
				) : null}
				comment={!validEmail ? null : (validEmail?.user ? <>
					Ya existe un usuario con este correo electrónico. <Link to={`/users/${validEmail.user.user_id}`}>Ver usuario</Link>
				</> : warnEmailExternal ? (
					'El correo electrónico no pertenece a "arema.mx", favor de asegurarse que se desea dar acceso de administrador a este usuario externo'
				) : validEmail.error ? (
					'Hubo un error checando la disponibilidad del correo electrónico'
				) : null) as any}
				buttons={(
					<Button icon iconName={validEmail ? (validEmail.error ? 'triangle-exclamation' : (validEmail.user ? 'times-circle' : 'check-circle')) : 'envelope'} loading={validEmail?.searching} />
				)}
			/>
			{/* <Field label='Correo electrónico'>
				{!!validEmail && !validEmail.searching && (validEmail.error || validEmail.user || warnEmailExternal) && (
					<div className={classNames("comment", {
						info: (validEmail.error || warnEmailExternal),
						error: validEmail.user
					})} style={{ marginTop: 2 }}>
						{validEmail.user ? <>
							Ya existe un usuario con este correo electrónico. <Link to={`/users/${validEmail.user.user_id}`}>Ver usuario</Link>
						</> : warnEmailExternal ? (
							'El correo electrónico no pertenece a "arema.mx", favor de asegurarse que se desea dar acceso de administrador a este usuario externo'
						) : (
							'Hubo un error checando la disponibilidad del correo electrónico'
						)}
					</div>
				)}
			</Field> */}
			{/* <Checkbox checked={userData.send_password} onChange={onFormChange('send_password')} label={'Enviar contraseña por correo'} style={{ marginTop: 15, marginBottom: 15 }} /> */}
			{!userData.send_password ? <>
				<Input label='Contraseña' value={userData.password} onChange={onFormChange('password')} />	
				<Message type='info'>
					El sistema le pedirá al usuario cambiar su contraseña la primera vez que entre al sistema
				</Message>
			</> : (
				<Message type='info' style={{ marginTop: 15 }}>
					Se le enviará al usuario un correo electrónico con información de como entrar a su cuenta
				</Message>
			)}
			{!!canGrantAccess && (
				<Table style={{ marginTop: 20 }}>
					<Table.Head
						title='Accesos' 
						collapsingIndexes={[0]} 
					/>
					<Table.Body>
						{SYSTEM_ACCESS.filter(a=>hasAccess(a.access)).map(a=>(
							<SystemAccessRow key={`SYSAC-${a.system}`} system={a.system} value={!!systemAccess[a.system]} onChange={onSystemAccessChange(a.system)} />
						))}
					</Table.Body>
				</Table>
			)}
			<Message style={{ marginTop: 30 }} type='error' list={errorPrompts} />
		</Groupper>
	</div>
}

export default UserCreate;